import { useCallback, useEffect, useMemo } from "react";

import { useQuery } from "hooks";

import {
  toClientCardTask,
  toConditionsGroupEvaluable,
} from "scenes/Hubly/components/Workspace/api";
import toGlobalId from "scenes/Hubly/components/Workspace/api/helper";

export function useClientCardTasksQuery(
  clientWorkflowId,
  onDone,
  queryNumComments = false
) {
  const queryFactory = useCallback(() => {
    const commentsSubquery = queryNumComments
      ? ""
      : "comments {\n          edges {\n            node {\n              uuid\n            }\n          }\n        }";
    const query = `
    fragment TaskFields on Task {
        uuid
        title
        clientWorkflow {
          uuid
        }
        advisorDefinedCompletedAt
        description
        options
        completed
        completedAt
        isClientTask
        assignee {
          uuid
          name
          group
        }
        assignedAdvisor {
          uuid
          firstName
          lastName
        }
        order
        householdMembers {
          edges {
            node {
              uuid
              title
              type
              client {
                uuid
                firstName
                lastName
                name
              }
              household {
                uuid
              }
            }
          }
        }
        timeEstimates {
          edges {
            node {
              uuid
              autoAddedInd
              timeEstimate
              createdAt
              addedBy {
                uuid
                firstName
                lastName
              }
            }
          }
        }
        emailTemplate {
          uuid
          to
          subject
          body
        }
        reminders {
          edges {
            node {
              uuid
              time
              futureReminderNumber
              futureReminderType
              futureReminderTime
              task {
                uuid
              }
              dismissed
              completedAt
            }
          }
        }
        links {
          edges {
            node {
              uuid
              title
              url
            }
          }
        }
        numComments
        ${commentsSubquery}
        formInstance {
          uuid
          isFilled
          name
        }
      }
      
      query getClientWorkflowTasks($id: ID!) {
        clientWorkflow(id: $id) {
          uuid
          numComments
          workflow {
            name
          }
          tasksGroup {
            ... on ConditionsGroupEvaluable {
              id
              task {
                edges {
                  node {
                    ...TaskFields
                  }
                }
              }
              conditions(first: 10) {
                edges {
                  node {
                    id
                    operator
                    operand
                    type
                    name
                    datePart
                    workflowPart
                    formFieldValue {
                      name
                      type {
                        id
                      }
                    }
                    value {
                      ... on StringValue {
                        stringValue
                      }
                      ... on ArrayStringValue {
                        arrayStringValue
                      }
                    }
                    tags {
                      edges {
                        node {
                          id
                          name
                        }
                      }
                    }
                    streams {
                      edges {
                        node {
                          id
                          name
                          color
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on Task {
              ...TaskFields
            }
          }
        }
      }`;

    const resolver = ({ data: { clientWorkflow: cw } }) => {
      return {
        tasksGroup: cw.tasksGroup.map((group) => {
          return group?.conditions
            ? toConditionsGroupEvaluable(group, cw)
            : toClientCardTask(group, cw);
        }),
        numWorkflowComments: cw.numComments,
        cwId: cw.uuid,
      };
    };

    return { query, resolver };
  }, [queryNumComments]);

  const {
    reloadData,
    data: { cwId = null, tasksGroup = [], numWorkflowComments = 0 },
    loading,
  } = useQuery(queryFactory, {
    variables: useMemo(() => {
      return { id: toGlobalId("ClientWorkflow", clientWorkflowId) };
    }, [clientWorkflowId]),
    options: { fetchOnInit: false },
  });

  useEffect(() => {
    if (!loading && cwId) {
      onDone(cwId, tasksGroup, numWorkflowComments);
    }
  }, [loading, cwId, tasksGroup, onDone, numWorkflowComments]);

  return { reloadData, loading };
}

export default useClientCardTasksQuery;
